import { Card, CardContent, Grid, Typography, useTheme } from "@mui/material";
import React from "react";
import { useConfig } from "../../contexts/ConfigContext";


interface HomePageProps {
  onCardClick: (tabIndex: number, options: { tableName?: string }) => void;
}

const HomePage: React.FC<HomePageProps> = ({ onCardClick }) => {
  const config = useConfig();
  const theme = useTheme();
  
  const cards = config.homePageShortcuts ?? [];
  
  const handleCardClick = (card: any) => {
    // Debug logging
    console.log('[Home] Card clicked:', card);
    console.log('[Home] Looking for endpoint matching name:', card.name);
    console.log('[Home] Or tableName:', card.options.tableName);
    
    // First try to find endpoint by name
    let endpoint = config.apiEndpoints?.find(
      ep => ep.name.toLowerCase() === card.name.toLowerCase()
    );
    
    // If not found, try by tableName
    if (!endpoint && card.options.tableName) {
      endpoint = config.apiEndpoints?.find(
        ep => ep.tableName.toLowerCase() === card.options.tableName.toLowerCase()
      );
    }
    
    console.log('[Home] Found endpoint:', endpoint);
    
    // Use the endpoint's tableName if found, otherwise use the original tableName
    const tableName = endpoint?.tableName || card.options.tableName;
    console.log('[Home] Using tableName:', tableName);
    
    // Create final options with all original options but updated tableName
    const finalOptions = {
      ...card.options,
      tableName
    };
    
    onCardClick(card.targetTab, finalOptions);
  };

  return (
    <Card
      sx={{
        width: "70vw", // 80% of 80vw
        maxWidth: "1400px", // 80% of 1400px
        minHeight: "240px", // 80% of 300px
        maxHeight: "72vh", // 80% of 90vh
        overflow: "auto",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        // Modern glass-morphism effect
        backgroundColor: "rgba(26, 26, 26, 0.9)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        borderRadius: "16px",
        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        '&:hover': {
          boxShadow: "0 12px 48px rgba(0, 0, 0, 0.4)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
        }
      }}
    >
      <CardContent sx={{ padding: "24px !important" }}>
        <Typography
          variant="h1"
          component="h2"
          gutterBottom
          align="center"
          sx={{ 
            fontSize: "1.8em", // 80% of 2.2em
            margin: "0.6em 0", // 80% of 0.8em
            fontWeight: 700,
            color: "white",
            textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
            letterSpacing: "0.02em"
          }}
        >
          {config.homePageTitle ?? "Culture Map Demo"}
        </Typography>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={5} container direction="column" alignItems="center">
            <img
              src={config.loginImagePath ?? "icons/lock.png"}
              alt="Login Image"
              style={{ 
                width: "240px", // 80% of 300px
                maxWidth: "100%", 
                padding: "16px", // 80% of 20px
                filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))",
                borderRadius: "12px"
              }}
            />
            <Typography 
              variant="body1" 
              align="justify" 
              sx={{ 
                px: 1.6, // 80% of 2
                color: "rgba(255, 255, 255, 0.9)",
                lineHeight: 1.6,
                fontSize: "0.9rem", // 80% of 1.1rem
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)"
              }}
            >
              {config.homePageDescription ?? "Welcome to Culture Map Demo..."}
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <Grid 
              container 
              spacing={3}
              justifyContent="flex-start"
              alignItems="flex-start"
              sx={{ 
                height: '100%',
                padding: { xs: '8px', md: '16px' }
              }}
            >
              {cards.map((card, index) => (
                <Grid
                  item
                  xs={6}
                  sm={4}
                  md={4}
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}
                >
                  <Card
                    onClick={() => handleCardClick(card)}
                    sx={{
                      height: { xs: 104, sm: 120, md: 128 }, // 80% of original sizes
                      width: '100%',
                      maxWidth: { xs: 104, sm: 120, md: 128 }, // 80% of original sizes
                      // Subtle radial gradient across full card (1.5x diameter)
                      background: `radial-gradient(ellipse 150% 150% at center, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.dark} 100%)`,
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.25)",
                      borderRadius: "12px",
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': {
                        transform: 'translateY(-6px) scale(1.05)',
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                        boxShadow: "0 16px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                        backdropFilter: "blur(12px)",
                        '& .MuiTypography-root': {
                          textShadow: "0 2px 8px rgba(0, 0, 0, 0.6)",
                          transform: 'scale(1.02)',
                        }
                      },
                      '&:active': {
                        transform: 'translateY(-3px) scale(1.03)',
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                        transition: 'all 0.1s ease-out',
                      }
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        padding: '16px !important', // 80% of 20px
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        align="center" 
                        sx={{
                          color: "white",
                          wordBreak: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          fontSize: { xs: '0.76rem', sm: '0.84rem', md: '0.92rem' }, // 80% of original sizes
                          fontWeight: 600,
                          letterSpacing: "0.01em",
                          textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                          lineHeight: 1.2
                        }}
                      >
                        {card.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default HomePage;
